import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Palette, Save, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WhiteLabelSettings = ({ tenant, onUpdate }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(tenant?.logo_url || null);
  const [primaryColor, setPrimaryColor] = useState(tenant?.primary_color || '#3b82f6');
  const [appName, setAppName] = useState(tenant?.app_name || tenant?.company_name || 'Flotto');
  const [saving, setSaving] = useState(false);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo too large (max 2MB)');
        return;
      }
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      let logoUrl = tenant?.logo_url;
      
      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        formData.append('type', 'logo');
        
        const uploadResponse = await axios.post(
          `${API_URL}/api/super-admin/tenants/${tenant.tenant_id}/upload-logo`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        logoUrl = uploadResponse.data.logo_url;
      }
      
      // Update branding
      await axios.patch(
        `${API_URL}/api/super-admin/tenants/${tenant.tenant_id}`,
        {
          logo_url: logoUrl,
          primary_color: primaryColor,
          app_name: appName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('White-Label settings saved!');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Save branding error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>White-Label Settings</CardTitle>
          <Badge variant="secondary">Enterprise Feature</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div>
          <Label htmlFor="logo">Company Logo</Label>
          <div className="mt-2 flex items-center gap-4">
            {logoPreview && (
              <div className="h-20 w-20 border rounded-lg p-2 bg-white flex items-center justify-center">
                <img src={logoPreview} alt="Logo Preview" className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <div className="flex-1">
              <Input
                id="logo"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoUpload}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">PNG, JPG, or SVG. Max 2MB.</p>
            </div>
          </div>
        </div>

        {/* Primary Color */}
        <div>
          <Label htmlFor="color">Primary Color</Label>
          <div className="mt-2 flex items-center gap-4">
            <Input
              id="color"
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-20 h-12 cursor-pointer"
            />
            <Input
              type="text"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#3b82f6"
              className="flex-1"
            />
            <div 
              className="h-12 w-12 rounded-lg border"
              style={{ backgroundColor: primaryColor }}
            />
          </div>
        </div>

        {/* App Name */}
        <div>
          <Label htmlFor="appName">App Name (Optional)</Label>
          <Input
            id="appName"
            type="text"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="Your Company Name"
            className="mt-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Shown in app header and login page
          </p>
        </div>

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-zinc-50 dark:bg-zinc-900">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4" />
            <span className="text-sm font-medium">Preview</span>
          </div>
          <div className="flex items-center gap-3">
            {logoPreview && (
              <img src={logoPreview} alt="Logo" className="h-10 w-10 object-contain" />
            )}
            <div>
              <div className="font-bold text-lg" style={{ color: primaryColor }}>
                {appName}
              </div>
              <div className="text-xs text-gray-500">{tenant?.subdomain}.flotto.app</div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save White-Label Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WhiteLabelSettings;
